import { formatToTimeZone } from '@zenchef/date-fns-timezone'
import { addMonths, differenceInDays, differenceInMonths, isBefore, isSameMonth } from 'date-fns'
import { useContext } from 'react'

import AppStore from '@/stores/AppStore'
import { Bookings } from '@/types/types'
import { computeDate, formatTime } from '@/utils/formatDateTime'
import getDateFnsLocal from '@/utils/getDateFnsLocal'
import StoresContext from '@/utils/StoresContext'

type AcceptedDateFormat = 'dddd D MMMM' | 'ddd D MMM' | 'dd D MMM'

export const getRelativeDateFormat = (date: string, restaurantTimezone: string): [string, Record<string, number>?] => {
  const today = formatToTimeZone(new Date(), 'YYYY-MM-DD', { timeZone: restaurantTimezone })
  const daysDiff = differenceInDays(date, today)

  if (isBefore(date, today)) {
    // we usually not show dates before today, but in case it happens we don't want to show "tomorrow" for "yesterday"
    // happened with widget not refreshed overnight
    // probably can happen with overnight shifts
    return ['global.x_days_ago', { count: daysDiff }]
  }

  if (isSameMonth(date, today) || daysDiff < 10) {
    return ['global.in_x_days', { count: daysDiff }]
  }

  const firstDayOfCurrentMonth = today.slice(0, 8) + '01'
  const firstDayOfNextMonth = addMonths(new Date(firstDayOfCurrentMonth), 1)

  if (isSameMonth(date, firstDayOfNextMonth)) {
    return ['global.in_x_months', { count: 1 }]
  }

  const monthsDiff = Math.max(differenceInMonths(date, today), 2)

  return ['global.in_x_months', { count: monthsDiff }]
}

export const formatDateAndSlot = (
  wish: Bookings.PreviousWish | Bookings.Wish,
  language: string,
  restaurantTimezone: string | undefined,
  format: AcceptedDateFormat
): { date: string | undefined; slot: string | undefined } => {
  const { day, slot } = wish
  if (!day || !slot) throw Error('calling formatDateAndSlot without day or slot')
  if (!restaurantTimezone) {
    throw Error('calling formatDateAndSlotToDisplayInCart before initialization')
  }

  const locale = getDateFnsLocal(language)
  const computedDate = computeDate(day, slot, restaurantTimezone)
  const formattedDate = formatToTimeZone(computedDate, format, {
    locale,
    timeZone: restaurantTimezone
  })
  const formattedSlot = formatTime(computedDate, language, restaurantTimezone)

  return { date: formattedDate, slot: formattedSlot }
}

export const getShortDateFormat = (language: string) => {
  return `${['nl', 'de'].includes(language.toLowerCase()) ? 'dd' : 'ddd'} D MMM` as const
}

export const getShorterDateFormat = (language: string) => {
  return `${['nl', 'de'].includes(language.toLowerCase()) ? 'dd' : 'ddd'} D` as const
}

export const formatDateAndSlotToDisplayInCart = (
  wish: Bookings.PreviousWish | Bookings.Wish,
  language: string,
  restaurantTimezone: string | undefined
) => {
  return formatDateAndSlot(wish, language, restaurantTimezone, getShortDateFormat(language))
}

export const getFormattedDateAndTime = (appStore: AppStore) => {
  const { language, restaurantTimezone, wish } = appStore.state

  return formatDateAndSlot(wish, language, restaurantTimezone, 'dddd D MMMM')
}

export default function useFormattedDateAndTime() {
  const { appStore } = useContext(StoresContext)
  return getFormattedDateAndTime(appStore)
}
